import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { clearSettingsCache } from "@/hooks/useTrialInfo";
import AdminFeatureFlags from "@/pages/AdminFeatureFlags";
import { 
  Settings, ToggleRight, DollarSign, Bell, Shield, Wrench, Download, Upload,
  Minus, Plus, RefreshCw, Headphones, Check
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Improved text input component with proper typing support and matching style
function TextInput({ value, onChange, placeholder = "", testId }) {
  const [localValue, setLocalValue] = useState(value || "");
  const [isSaved, setIsSaved] = useState(false);
  
  const handleChange = (e) => {
    setLocalValue(e.target.value);
    setIsSaved(false);
  };
  
  const handleBlur = () => {
    // On blur, commit the value
    if (localValue !== value) {
      onChange(localValue);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-64 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
        data-testid={testId}
      />
      {isSaved && (
        <span className="text-emerald-600 flex items-center gap-1 text-sm">
          <Check className="h-4 w-4" />
          Saved
        </span>
      )}
    </div>
  );
}

// Improved numeric input component with proper typing support
function NumericInput({ value, onChange, min = 0, max = 999999, step = 1, testId }) {
  const [localValue, setLocalValue] = useState(value?.toString() || "0");
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    // Allow empty string for typing, or valid numbers
    if (newValue === "" || newValue === "-" || /^-?\d*$/.test(newValue)) {
      setLocalValue(newValue);
    }
  };
  
  const handleBlur = () => {
    // On blur, validate and commit the value
    let numValue = parseInt(localValue, 10);
    if (isNaN(numValue)) numValue = min;
    if (numValue < min) numValue = min;
    if (numValue > max) numValue = max;
    setLocalValue(numValue.toString());
    onChange(numValue);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
    // Allow arrow keys to increment/decrement
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const newVal = Math.min(max, (parseInt(localValue, 10) || 0) + step);
      setLocalValue(newVal.toString());
      onChange(newVal);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const newVal = Math.max(min, (parseInt(localValue, 10) || 0) - step);
      setLocalValue(newVal.toString());
      onChange(newVal);
    }
  };
  
  const increment = () => {
    const newVal = Math.min(max, (parseInt(localValue, 10) || 0) + step);
    setLocalValue(newVal.toString());
    onChange(newVal);
  };
  
  const decrement = () => {
    const newVal = Math.max(min, (parseInt(localValue, 10) || 0) - step);
    setLocalValue(newVal.toString());
    onChange(newVal);
  };
  
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={decrement}
        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
        aria-label="Decrease value"
      >
        <Minus className="h-3 w-3" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-24 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-center font-mono focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={increment}
        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
        aria-label="Increase value"
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}

const getSettingCategoryIcon = (category) => {
  switch (category) {
    case "feature_flags": return <ToggleRight className="h-5 w-5" />;
    case "pricing": return <DollarSign className="h-5 w-5" />;
    case "notifications": return <Bell className="h-5 w-5" />;
    case "security": return <Shield className="h-5 w-5" />;
    case "integrations": return <Wrench className="h-5 w-5" />;
    case "support": return <Headphones className="h-5 w-5" />;
    default: return <Settings className="h-5 w-5" />;
  }
};

export default function SettingsTab({
  settingsGrouped,
  onToggleSetting,
  onUpdateSetting
}) {
  const [activeSettingCategory, setActiveSettingCategory] = useState("feature_flags");

  const handleExportDatabase = async () => {
    try {
      const res = await fetch(`${API}/api/admin/auth/export-database`, {
        method: "GET",
        credentials: "include"
      });
      const result = await res.json();
      if (result.success) {
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `eduzy-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Database exported! ${Object.values(result.data.summary).reduce((a,b) => a+b, 0)} records.`);
      } else {
        toast.error(result.detail || "Failed to export database");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export database");
    }
  };

  const handleImportDatabase = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!window.confirm("This will MERGE the imported data:\n\n- Update existing records (matched by ID)\n- Add new records from import\n- Keep records only in this database\n\nProceed?")) {
      e.target.value = '';
      return;
    }
    
    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      const res = await fetch(`${API}/api/admin/auth/import-database`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ data: importData })
      });
      const result = await res.json();
      if (result.success) {
        toast.success(`Database merged! Inserted: ${result.totals.inserted}, Updated: ${result.totals.updated}, Unchanged: ${result.totals.unchanged}`);
        console.log("Import result:", result);
      } else {
        toast.error(result.detail || "Failed to import database");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import database: " + error.message);
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-4" data-testid="settings-tab-content">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">System Settings</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            clearSettingsCache();
            toast.success("Settings cache cleared. All pages will fetch fresh values.");
          }}
          className="border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Clear Frontend Cache
        </Button>
      </div>
      
      <p className="text-sm text-gray-500">
        Changes here automatically update across all marketing pages, FAQs, pricing, and email templates.
      </p>
      
      {/* Database Tools Section */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Wrench className="h-5 w-5 text-orange-500" />
            Database Tools
          </CardTitle>
          <CardDescription className="text-gray-500">
            Export/Import database for backup and sync between environments.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Export Database */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Export Database</p>
              <p className="text-xs text-gray-500">
                Download all parent/student data as JSON. Use this to backup or sync to another environment.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white"
              onClick={handleExportDatabase}
              data-testid="export-database-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>

          {/* Import Database */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Import Database</p>
              <p className="text-xs text-gray-500">
                Upload JSON export to sync from another environment.
                <br />
                <span className="text-emerald-600">Safe merge: Updates existing, adds new, preserves unique records.</span>
              </p>
            </div>
            <div className="flex gap-2">
              <input
                type="file"
                accept=".json"
                id="import-file-input"
                className="hidden"
                onChange={handleImportDatabase}
              />
              <Button
                variant="outline"
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white"
                onClick={() => document.getElementById('import-file-input')?.click()}
                data-testid="import-database-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Category Sidebar */}
        <Card className="bg-white border-gray-200 shadow-sm lg:col-span-1">
          <CardContent className="p-4 space-y-2">
            {Object.keys(settingsGrouped).map((category) => (
              <button
                key={category}
                onClick={() => setActiveSettingCategory(category)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeSettingCategory === category
                    ? "bg-red-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                data-testid={`settings-category-${category}`}
              >
                {getSettingCategoryIcon(category)}
                <span className="capitalize">{category.replace("_", " ")}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Settings Panel */}
        <Card className="bg-white border-gray-200 shadow-sm lg:col-span-3">
          <CardHeader>
            <CardTitle className="capitalize flex items-center gap-2 text-gray-900">
              {getSettingCategoryIcon(activeSettingCategory)}
              {activeSettingCategory?.replace("_", " ")} Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {settingsGrouped[activeSettingCategory]?.map((setting) => (
              <div
                key={setting.key}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-gray-900">{setting.description}</p>
                  <p className="text-xs text-gray-500 font-mono">{setting.key}</p>
                </div>
                <div className="flex-shrink-0">
                  {setting.type === "boolean" ? (
                    <button
                      onClick={() => onToggleSetting(setting.key, setting.value)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        setting.value ? "bg-emerald-500" : "bg-gray-300"
                      }`}
                      data-testid={`toggle-${setting.key}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                          setting.value ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  ) : setting.type === "number" ? (
                    <NumericInput
                      value={setting.value}
                      onChange={(val) => onUpdateSetting(setting.key, val)}
                      min={setting.key.includes("limit") ? -1 : 0}
                      max={setting.key.includes("days") ? 365 : 999999}
                      step={setting.key.includes("days") ? 1 : setting.key.includes("minutes") ? 5 : 1}
                      testId={`input-${setting.key}`}
                    />
                  ) : (
                    <TextInput
                      value={setting.value}
                      onChange={(val) => onUpdateSetting(setting.key, val)}
                      placeholder={setting.description}
                      testId={`input-${setting.key}`}
                    />
                  )}
                </div>
              </div>
            ))}
            {(!settingsGrouped[activeSettingCategory] || settingsGrouped[activeSettingCategory].length === 0) && activeSettingCategory !== "feature_flags" && (
              <p className="text-center text-gray-500 py-8">No settings in this category</p>
            )}

            {activeSettingCategory === "feature_flags" && (
              <div className="pt-2 border-t border-gray-100" data-testid="settings-feature-flags-engine">
                <AdminFeatureFlags />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
